<template>
  <v-dialog
    :value="value"
    :max-width="dialogWidth"
    :fullscreen="isMobile()"
    scrollable
    @click:outside="$emit('close')"
    @keydown.esc="$emit('close')"
  >
    <v-card :height="cardHeight">
      <v-card-title>
        {{ $t('location.location') }}
        <GeofenceResultInfoMenu v-if="infoMenuActive"/>
        <v-spacer/>
        <v-btn icon @click="$emit('close')">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-title class="py-2">
        <v-chip
          v-show="assignmentGeofences.length > 0 || shiftGeofence.length > 0"
          v-for="marker in markerResults"
          v-bind:key="marker.key"
          :color="marker.color"
          outlined
          class="mr-2 mb-2"
        >
          {{ marker.message }}
        </v-chip>
      </v-card-title>

      <v-divider/>

      <v-card-text class="pa-4">
        <MapLoader
          :markers="markers"
          :geofences="shiftGeofence"
        />
      </v-card-text>
      <v-divider/>

      <v-card-actions class="py-3">
        <v-spacer/>
        {{ $t('location.avgAccuracy', {accuracy: avgAccuracy}) }}
        <v-spacer/>
        <v-spacer v-if="isGeofenceFeatureActive">
          <v-spacer
            v-if="(shiftGeofence.length > 0) && timePairInGeoFence()">
            {{ $tc('location.shiftGeofence', shiftGeofence.length, {
            geofence: shiftGeofence.map((it) => ' ' + it.name),
          }) }}
          </v-spacer>
          <v-spacer
            v-else
          >
            {{
              $tc(
                'location.assignedGeofence',
                assignmentGeofences.length,
                {geofence: assignmentGeofences.length === 0
                    ? 'N/A' : assignmentGeofences.map((it) => ' ' + it.name)}
              )
            }}
          </v-spacer>
        </v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

import capitalize from 'lodash/capitalize';
import filter from 'lodash/filter';
import map from 'lodash/map';
import some from 'lodash/some';

import isMobile from '@/mixins/displayAdaptable';

import LocationUtil from '@/util/location-util';

import MapLoader from '@/components/MapLoader.vue';
import constants from '@/constants';
import GeofenceResultInfoMenu from '@/components/GeofenceResultInfoMenu.vue';
import attachMobileBackButton from '@/mixins/attachMobileBackButton';
import hasLocationFeature from '@/mixins/location/hasLocationFeature';

export default {
  name: 'TimePairMapDialog',
  components: { GeofenceResultInfoMenu, MapLoader },
  mixins: [attachMobileBackButton, isMobile, hasLocationFeature],
  props: {
    value: Boolean,
    timePair: Object,
  },

  data() {
    return {
      clockInIcon: 'https://maps.google.com/mapfiles/kml/paddle/go.png',
      clockOutIcon: 'https://maps.google.com/mapfiles/kml/paddle/stop.png',
    };
  },

  computed: {

    dialogWidth() {
      switch (this.$vuetify.breakpoint.name) {
        case 'lg':
        case 'xl': {
          return '50%';
        }
        default: {
          return '100%';
        }
      }
    },

    cardHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xl': {
          return '750px';
        }
        default: {
          return '600px';
        }
      }
    },

    avgAccuracy() {
      if ((this.timePair.clockIn && this.timePair.clockIn.accuracy > 0)
        && (this.timePair.clockOut && this.timePair.clockOut.accuracy > 0)) {
        return LocationUtil.getMeasuredAccuracy(
          (this.timePair.clockIn.accuracy + this.timePair.clockOut.accuracy) / 2,
        );
      }
      if (this.timePair.clockIn && this.timePair.clockIn.accuracy > 0) {
        return LocationUtil.getMeasuredAccuracy(this.timePair.clockIn.accuracy);
      }
      return '-';
    },

    assignmentGeofences() {
      let geofences = [];

      if (this.markers.filter(
        (it) => it.geofenceResult === constants.LocationData.GeofenceResult.NO_GEOFENCE,
      ).length === 2) {
        return geofences;
      }

      if (this.timePair.employeeAssignment) {
        geofences = geofences.concat(this.timePair.employeeAssignment.geofences);
      }
      return geofences;
    },

    shiftGeofence() {
      const geofences = [];

      if (this.timePair.clockIn && this.timePair.clockIn.geofence) {
        geofences.push(this.timePair.clockIn.geofence);
      }

      if (this.timePair.clockOut && this.timePair.clockOut.geofence
        && (!(this.timePair.clockIn && this.timePair.clockIn.geofence)
          || (this.timePair.clockOut.geofence.name !== this.timePair.clockIn.geofence.name))) {
        geofences.push(this.timePair.clockOut.geofence);
      }

      if (geofences.length > 0) {
        return [...new Set(geofences)];
      }
      return [];
    },

    markers() {
      const markers = [];
      markers.push({
        title: this.$t('clock.clockIn'),
        position: { lat: this.getClockInLatitude(), lng: this.getClockInLongitude() },
        icon: this.clockInIcon,
        geofenceResult: this.getTimeEntryGeofenceResult(this.timePair.clockIn),
      });

      if (this.timePair.clockOut) {
        markers.push({
          title: this.$t('clock.clockOut'),
          position: { lat: this.getClockOutLatitude(), lng: this.getClockOutLongitude() },
          icon: this.clockOutIcon,
          geofenceResult: this.getTimeEntryGeofenceResult(this.timePair.clockOut),
        });
      }

      return markers;
    },

    markerResults() {
      return map(
        this.markers.filter((it) => it.geofenceResult !== constants.LocationData.GeofenceResult.NO_GEOFENCE),
        (marker) => {
          const key = marker.title;

          if (marker.position.lat === -1 && marker.position.lng === -1) {
            return {
              key,
              color: `${constants.AppColors.ERROR} darken-1`,
              message: `${capitalize(marker.title)} location not provided`,
            };
          }

          let markerColor;

          switch (marker.geofenceResult) {
            case constants.LocationData.GeofenceResult.OUTSIDE:
              markerColor = constants.AppColors.ERROR;
              break;
            case constants.LocationData.GeofenceResult.INSIDE:
              markerColor = constants.AppColors.SUCCESS;
              break;
            default:
              markerColor = constants.AppColors.WARN;
          }

          return {
            key,
            color: `${markerColor} darken-1`,
            message: this.$t('location.markerLocationIs', {
              marker: marker.title,
              geoLocation: this.$t(`location.geoLocation.${marker.geofenceResult}`),
            }),
          };
        },
      );
    },

    infoMenuActive() {
      if (this.assignmentGeofences.length === 0 || this.shiftGeofence.length === 0) return false;

      return some(
        filter(
          this.markers,
          (marker) => marker.geofenceResult !== constants.LocationData.GeofenceResult.INSIDE,
        ),
        (marker) => (marker.geofenceResult === constants.LocationData.GeofenceResult.BORDERING_INSIDE
          || marker.geofenceResult === constants.LocationData.GeofenceResult.BORDERING_OUTSIDE),
      );
    },
  },

  methods: {
    getClockInLatitude() {
      return this.timePair.clockIn
        ? this.offsetMarkerFromCollision(this.timePair.clockIn.latitude, this.getClockOutLatitude())
        : -1;
    },

    getClockInLongitude() {
      return this.timePair.clockIn
        ? this.offsetMarkerFromCollision(this.timePair.clockIn.longitude,
          this.getClockOutLongitude())
        : -1;
    },

    getClockOutLatitude() {
      return this.timePair.clockOut ? this.timePair.clockOut.latitude : -1;
    },

    getClockOutLongitude() {
      return this.timePair.clockOut ? this.timePair.clockOut.longitude : -1;
    },

    offsetMarkerFromCollision(value, otherValue, tolerance) {
      if (!value) {
        return -1;
      }

      const markersColliding = value === otherValue;
      const markersTooClose = Math.abs(value - otherValue) < tolerance;

      if (markersColliding || markersTooClose) {
        return value + 0.00005;
      }
      return value;
    },

    getTimeEntryGeofenceResult(timeEntry) {
      return timeEntry ? timeEntry.geofenceResult : constants.LocationData.GeofenceResult.NO_GEOFENCE;
    },

    timePairInGeoFence() {
      return (this.timePair.clockIn && this.timePair.clockIn.geofenceResult
        !== constants.LocationData.GeofenceResult.OUTSIDE && this.timePair.clockIn.geofenceResult
      !== constants.LocationData.GeofenceResult.NO_GEOFENCE) || (
        this.timePair.clockOut
          && this.timePair.clockOut.geofenceResult !== constants.LocationData.GeofenceResult.OUTSIDE
         && this.timePair.clockOut.geofenceResult !== constants.LocationData.GeofenceResult.NO_GEOFENCE
      );
    },
  },
};
</script>
